import { useState } from "react";
import { Link } from "react-router-dom";
import { ecoApi } from "../lib/api";
import { useCompany } from "../masters/reference";
import { errText, useLoad } from "../ui/useLoad";
import { ErrorNote, Loading, PageHead } from "../ui/bits";
import { fmtAmount } from "../lib/money";
import { toUrl } from "../shell/routes";

// EC10: sales through e-commerce operators. Section 52: we charge the tax, the operator collects TCS (0.5% of the net
// value) and pays it in for us; booking it moves it from the operator's account to "GST TCS receivable", which becomes
// cash-ledger credit once the operator files GSTR-8 and it is accepted on the portal. Section 9(5): the operator pays the
// tax on notified services; we only report the value.
const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const n = (x: number) => fmtAmount(x);
const month = (s: string) => new Date(s + "T00:00:00").toLocaleDateString("en-IN", { month: "long", year: "numeric" });

export function Ecommerce() {
  const { company } = useCompany();
  const t = new Date();
  const [from, setFrom] = useState(iso(new Date(t.getFullYear(), t.getMonth() - 2, 1)));
  const [to, setTo] = useState(iso(t));
  const { data, error, reload } = useLoad(() => ecoApi.get(company.id, from, to), [company.id, from, to]);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const book = async (op: string, reg: string, m: string) => {
    setErr(null); setBusy(`${op}${reg}${m}`);
    try { await ecoApi.book(company.id, op, reg, m); await reload(); } catch (e) { setErr(errText(e)); } finally { setBusy(null); }
  };
  if (!data) return <section className="page"><PageHead crumb="Compliance" title="E-commerce operators" code="EC10" />{error ? <ErrorNote text={error.message} /> : <Loading />}</section>;
  return (
    <section className="page">
      <PageHead crumb="Compliance" title="E-commerce operators" code="EC10" />
      <p className="muted">Sales through an operator are marked on the invoice ("Sold through"). <b>Section 52</b>: you charge the tax; the operator keeps 0.5% as TCS and
        deposits it for you. <b>Section 9(5)</b>: for notified services (passenger transport, accommodation, housekeeping, restaurant, local delivery) the operator pays the tax.</p>
      {data.operators.length === 0 ? <div className="card empty-card"><p>No operators yet. Open the operator's account (AC11), give its GSTIN and tick <b>E-commerce operator</b>.</p></div> : <>
        <div className="toolbar">
          <label className="inline-label">From<input type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></label>
          <label className="inline-label">To<input type="date" value={to} onChange={(e) => setTo(e.target.value)} /></label>
          <span className="muted small">{data.operators.map((o) => `${o.name} (${o.gstin})`).join(" · ")}</span>
        </div>
        <ErrorNote text={err} />
        {data.rows.length === 0 ? <p className="muted">No sales through an operator in this period.</p> : (
          <table className="grid-table">
            <thead><tr><th>Month</th><th>Operator</th><th>Your GSTIN</th><th className="num">Section 52 sales</th><th className="num">TCS due</th>
              <th className="num">TCS booked</th><th className="num">Section 9(5) sales</th><th></th></tr></thead>
            <tbody>{data.rows.map((r) => {
              const due = r.tcs_igst + r.tcs_cgst + r.tcs_sgst, booked = r.booked_igst + r.booked_cgst + r.booked_sgst, left = Math.round((due - booked) * 100) / 100;
              const key = `${r.eco_ledger_id}${r.registration_id}${r.month}`;
              return (
                <tr key={key}>
                  <td>{month(r.month)}</td>
                  <td>{r.operator}<div className="muted small mono">{r.eco_gstin}</div></td>
                  <td className="mono small">{r.gstin}</td>
                  <td className="num">{n(r.sec52_value)}</td>
                  <td className="num">{n(due)}<div className="muted small">{r.rate}%{r.tcs_igst ? ` · IGST ${n(r.tcs_igst)}` : ""}{r.tcs_cgst ? ` · CGST ${n(r.tcs_cgst)} · SGST ${n(r.tcs_sgst)}` : ""}</div></td>
                  <td className="num">{n(booked)}{r.booked_vouchers && <div className="muted small">{r.booked_vouchers}</div>}</td>
                  <td className="num">{n(r.sec95_value)}</td>
                  <td>{data.can.book && left > 0 && <button disabled={busy === key} onClick={() => void book(r.eco_ledger_id, r.registration_id, r.month)}>Book TCS {n(left)}</button>}
                    {left === 0 && due > 0 && <span className="pill state-filed">Booked</span>}</td>
                </tr>);
            })}</tbody>
          </table>)}
        <p className="muted small">Booking posts a journal: Dr GST TCS receivable (e-commerce), Cr the operator's account, so the operator's balance matches what it pays out.
          Check the figures against the operator's statement and the "TCS and TDS credit received" view on the GST portal before accepting it there.
          GSTR-1 table 14 and GSTR-3B 3.1.1 are filled from these sales (<Link to={toUrl("gst/gstr1")}>GS01</Link>).</p>
      </>}
    </section>
  );
}
